// src/hooks/useSubscription.js (v2 – config-driven, uses api)

import { useEffect, useMemo, useState, useCallback, useRef } from "react";

import api from "../services/api";
import { API_PATHS } from "../config";

/**
 * Hook to fetch and monitor subscription status.
 *
 * Returns normalized status, metadata, convenience flags, and manual refresh.
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - Fetch on mount if true
 * @returns {Object}
 *   - status: "ACTIVE" | "TRIALING" | "INACTIVE" | "UNKNOWN"
 *   - meta: subscription details from backend
 *   - flags: { isActive, isTrialing, inTrial, isCanceling }
 *   - loading, error
 *   - refresh(): manual refresh function
 */
export default function useSubscription({ enabled = true } = {}) {
  const [status, setStatus] = useState("UNKNOWN");
  const [meta, setMeta] = useState({
    stripe_subscription_id: null,
    subscription_cancel_at_period_end: false,
    subscription_cancel_at_iso: null,
    subscription_current_period_end_iso: null,
    trial_ends_at_iso: null,
    trial_days_left: 0,
  });
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || inFlightRef.current) return;

    inFlightRef.current = true;
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      const { data } = await api.get(API_PATHS.SUBSCRIPTION_STATUS);

      if (!mountedRef.current) return;

      setStatus(data.status ? String(data.status).toUpperCase() : "INACTIVE");

      setMeta({
        stripe_subscription_id: data.stripe_subscription_id || null,
        subscription_cancel_at_period_end: !!data.subscription_cancel_at_period_end,
        subscription_cancel_at_iso: data.subscription_cancel_at_iso || null,
        subscription_current_period_end_iso: data.subscription_current_period_end_iso || null,
        trial_ends_at_iso: data.trial_ends_at_iso || null,
        trial_days_left: Number.isFinite(data.trial_days_left)
          ? Number(data.trial_days_left)
          : 0,
      });
    } catch (e) {
      if (!mountedRef.current) return;

      setStatus("INACTIVE");
      setMeta({
        stripe_subscription_id: null,
        subscription_cancel_at_period_end: false,
        subscription_cancel_at_iso: null,
        subscription_current_period_end_iso: null,
        trial_ends_at_iso: null,
        trial_days_left: 0,
      });
      setError(e);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled]);

  // Initial load + when enabled changes
  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  const flags = useMemo(() => {
    const isActive = status === "ACTIVE";
    const isTrialing = status === "TRIALING";
    const inTrial = isTrialing && meta.trial_days_left > 0;
    const isCanceling = isActive && meta.subscription_cancel_at_period_end;

    return { isActive, isTrialing, inTrial, isCanceling };
  }, [status, meta]);

  return { status, meta, flags, loading, error, refresh };
}

/** Helper to format ISO dates for UI */
export const formatLocalDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};